import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  usePartnersReadCampaignReport,
  usePartnersReadCampaigns,
  usePartnersReadOffer,
} from '@/api/hooks';
import { type PartnerCampaign } from '@/api/routes/partners/partners.campaigns.api';
import LoadErrorState from '@/components/LoadErrorState';
import { campaignStatusLabel } from '@/domain/offers/utils';
import CampaignLeadsSection from '@/domain/results/components/CampaignLeadsSection';
import CampaignPicker from '@/domain/results/components/CampaignPicker';
import CampaignSummary from '@/domain/results/components/CampaignSummary';
import ClicksByDayChart from '@/domain/results/components/ClicksByDayChart';
import ReportActionsTable from '@/domain/results/components/ReportActionsTable';
import ReportIndicators from '@/domain/results/components/ReportIndicators';
import ReportSlicesTable from '@/domain/results/components/ReportSlicesTable';
import ResultsEmptyState from '@/domain/results/components/ResultsEmptyState';
import ResultsSkeleton from '@/domain/results/components/ResultsSkeleton';
import { type ReportPeriodDays } from '@/domain/results/types';
import {
  REPORT_PERIODS,
  getActionRows,
  getDailyClicks,
  getSlotRows,
  getVariantRows,
  parseReportPeriod,
  pickCampaign,
  toReportParams,
} from '@/domain/results/utils';
import { useTranslate } from '@/state/i18n';

/** Every campaign of an account fits one page today. */
const CAMPAIGNS_PAGE = { offset: 0, limit: 100 };

/**
 * `/results` (P19): read-only numbers per campaign and the contacts of who accepted to share.
 * The campaign and the window live in the URL (`?campaign=&days=`).
 */
const Results = () => {
  const t = useTranslate('domain.results.Results');
  const [searchParams, setSearchParams] = useSearchParams();
  const days = parseReportPeriod(searchParams.get('days'));
  const campaigns = usePartnersReadCampaigns(CAMPAIGNS_PAGE);
  const campaign = pickCampaign(campaigns.data?.items ?? [], searchParams.get('campaign'));
  const reportParams = useMemo(() => toReportParams(days), [days]);
  const report = usePartnersReadCampaignReport(campaign?.id, reportParams);
  const { data: offer } = usePartnersReadOffer(campaign?.offer.id);
  const collectsLeads = offer?.actions.some((action) => action.kind === 'lead') ?? false;

  const update = (next: { campaign?: string; days?: ReportPeriodDays }) =>
    setSearchParams(
      {
        ...(campaign ? { campaign: campaign.id } : {}),
        days: String(days),
        ...(next.campaign ? { campaign: next.campaign } : {}),
        ...(next.days ? { days: String(next.days) } : {}),
      },
      { replace: true },
    );

  const renderReport = (selected: PartnerCampaign) => {
    if (report.isLoading) return <ResultsSkeleton />;
    if (report.isError || !report.data) {
      return <LoadErrorState message={t('reportError')} onRetry={() => report.refetch()} />;
    }
    const variantRows = getVariantRows(report.data);
    return (
      <Stack gap={3}>
        <ReportIndicators
          totals={report.data.totals}
          collectsLeads={collectsLeads}
          showsCouponCopies={Boolean(offer?.benefit) || report.data.totals.couponCopies > 0}
        />
        <Box sx={sx.columns}>
          <ClicksByDayChart days={getDailyClicks(report.data)} />
          <ReportSlicesTable
            title={t('bySlot')}
            column={t('slotColumn')}
            rows={getSlotRows(report.data)}
          />
        </Box>
        <Box sx={sx.columns}>
          <ReportActionsTable rows={getActionRows(report.data)} />
          {variantRows.length ? (
            <ReportSlicesTable
              title={t('byVariant')}
              column={t('variantColumn')}
              rows={variantRows}
            />
          ) : null}
        </Box>
        {collectsLeads ? <CampaignLeadsSection key={selected.id} campaign={selected} /> : null}
      </Stack>
    );
  };

  const renderContent = () => {
    if (campaigns.isLoading) return <ResultsSkeleton />;
    if (campaigns.isError || !campaigns.data) {
      return <LoadErrorState message={t('loadError')} onRetry={() => campaigns.refetch()} />;
    }
    if (!campaign) return <ResultsEmptyState />;
    return (
      <Stack gap={3}>
        <CampaignPicker
          campaigns={campaigns.data.items}
          selectedId={campaign.id}
          onSelect={(next) => update({ campaign: next.id })}
        />
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h3" component="h2" sx={{ overflowWrap: 'anywhere' }}>
            {campaign.offer.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            · {campaignStatusLabel[campaign.status]}
          </Typography>
        </Stack>
        <CampaignSummary campaign={campaign} />
        {renderReport(campaign)}
      </Stack>
    );
  };

  return (
    <Stack gap={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
      >
        <Stack gap={0.5}>
          <Typography component="h1" variant="h2">
            {t('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('subtitle')}
          </Typography>
        </Stack>
        {campaign ? (
          <TextField
            select
            size="small"
            value={days}
            slotProps={{ select: { inputProps: { 'aria-label': t('period') } } }}
            onChange={(event) => update({ days: Number(event.target.value) as ReportPeriodDays })}
            sx={{ minWidth: 180 }}
          >
            {REPORT_PERIODS.map((option) => (
              <MenuItem key={option} value={option}>
                {t('lastDays', { count: option })}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Stack>
      {renderContent()}
    </Stack>
  );
};

export default Results;

const sx = {
  columns: {
    display: 'grid',
    gap: 3,
    alignItems: 'start',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 3fr) minmax(0, 2fr)' },
  },
};
